import { Badge } from "@/components/ui/badge";
import { Card, CardAction, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useGetMonthlyRevenue } from "@/api/revenue/get-monthly-revenue";
import { formatCurrency } from "@/lib/utils";

export function RevenueCard() {
    const currentYear = new Date().getFullYear();
    const { data: revenueData, isLoading, isError } = useGetMonthlyRevenue(currentYear);

    // Calculate last month's revenue and growth percentage
    let lastMonthRevenue = 0;
    let growthPercentage = 0;
    let isGrowing = true;
    let lastMonthName = "";

    if (revenueData && revenueData.length > 0) {
        // Sort by month to ensure we have the most recent data
        const sortedData = [...revenueData].sort((a, b) => {
            const [aYear, aMonth] = a.month.split('-').map(Number);
            const [bYear, bMonth] = b.month.split('-').map(Number);
            
            if (aYear !== bYear) return bYear - aYear;
            return bMonth - aMonth;
        });

        if (sortedData.length >= 1) {
            lastMonthRevenue = sortedData[0].total_income;
            lastMonthName = new Date(
                parseInt(sortedData[0].month.split('-')[0]), 
                parseInt(sortedData[0].month.split('-')[1]) - 1
            ).toLocaleString('default', { month: 'long' });

            // Calculate growth if we have data for the previous month
            if (sortedData.length >= 2) {
                const previousMonthRevenue = sortedData[1].total_income;
                if (previousMonthRevenue !== 0) {
                    growthPercentage = ((lastMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100;
                    isGrowing = growthPercentage >= 0;
                } else {
                    // If previous month revenue is 0, growth is infinity - set to 100% or a large number
                    growthPercentage = lastMonthRevenue > 0 ? 100 : 0;
                    isGrowing = lastMonthRevenue > 0;
                }
            } else {
                // No previous month data available
                growthPercentage = 0;
                isGrowing = true; // Default to neutral state
            }
        }
    }

    if (isLoading) {
        return (
            <Card className="@container/card">
                <CardHeader>
                    <CardDescription>Total Revenue</CardDescription>
                    <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">Loading...</CardTitle>
                    <CardAction>
                        <Badge variant="outline">...</Badge>
                    </CardAction>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1.5 text-sm">
                    <div className="line-clamp-1 flex gap-2 font-medium">Calculating revenue...</div>
                    <div className="text-muted-foreground">Last month data</div>
                </CardFooter>
            </Card>
        );
    }

    if (isError || !revenueData || revenueData.length === 0) {
        return (
            <Card className="@container/card">
                <CardHeader>
                    <CardDescription>Total Revenue</CardDescription>
                    <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">$0.00</CardTitle>
                    <CardAction>
                        <Badge variant="outline">N/A</Badge>
                    </CardAction>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1.5 text-sm">
                    <div className="line-clamp-1 flex gap-2 font-medium">No data available</div>
                    <div className="text-muted-foreground">Revenue data is not available</div>
                </CardFooter>
            </Card>
        );
    }

    // Determine if growth is exactly 0%
    const isSteady = Math.abs(growthPercentage) === 0;

    return (
        <Card className="@container/card">
            <CardHeader>
                <CardDescription>Last Month's Revenue</CardDescription>
                <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                    {formatCurrency(lastMonthRevenue)}
                </CardTitle>
                <CardAction>
                    <Badge variant="outline">
                        {isSteady ? null : isGrowing ? <TrendingUp /> : <TrendingDown />}
                        {Math.abs(growthPercentage).toFixed(1)}%
                    </Badge>
                </CardAction>
            </CardHeader>
            <CardFooter className="flex-col items-start gap-1.5 text-sm">
                <div className="line-clamp-1 flex gap-2 font-medium">
                    {isSteady 
                        ? `Steady compared to previous month`
                        : isGrowing 
                            ? `Trending up from previous month` 
                            : `Trending down from previous month`}
                    {!isSteady && (isGrowing ? <TrendingUp className="size-4" /> : <TrendingDown className="size-4" />)}
                </div>
                <div className="text-muted-foreground">Revenue for {lastMonthName}</div>
            </CardFooter>
        </Card>
    );
}